/**
 * HTML rendering and CSS styling for CardElement
 */

import type { CardElementOptions, CSSProperties, ThemeStyles, ButtonStyle, Locale } from '../../types';
import { getDisclaimerText } from '../../i18n';

// Renderer needs required fields for rendering
type RendererOptions = Required<
  Pick<CardElementOptions, 'buttonText' | 'placeholders'>
> &
  Partial<CardElementOptions>;

export class CardElementRenderer {
  private shadowRoot: ShadowRoot | null = null;

  constructor(
    private container: HTMLElement,
    private options: RendererOptions,
    private locale: Locale
  ) {}

  /**
   * Get or create the shadow root
   */
  getShadowRoot(): ShadowRoot {
    // If we already have a reference, return it
    if (this.shadowRoot) {
      return this.shadowRoot;
    }

    // Check if container already has a shadow root (from previous mount/re-render)
    if (this.container.shadowRoot) {
      this.shadowRoot = this.container.shadowRoot;
      return this.shadowRoot;
    }

    // Create new shadow root
    this.shadowRoot = this.container.attachShadow({ mode: 'open' });
    return this.shadowRoot;
  }

  /**
   * Render HTML template into shadow DOM
   */
  render(): void {
    const shadow = this.getShadowRoot();
    shadow.innerHTML = `
<div class="inflowpay-card-element">

  <!-- Card Data -->
  <div class="inflowpay-card-inp-wrap">
  
    <input
      type="text"
      id="inflowpay-card-number"
      class="inflowpay-card-input inflowpay-card-number"
      placeholder="${this.options.placeholders.cardNumber}"
      maxlength="19"
      inputmode="numeric"
      autocomplete="cc-number"
    />

    <input
      type="text"
      id="inflowpay-expiry"
      class="inflowpay-card-input inflowpay-card-expiry"
      placeholder="${this.options.placeholders.expiry}"
      maxlength="5"
      inputmode="numeric"
      autocomplete="cc-exp"
    />

    <input
      type="text"
      id="inflowpay-cvc"
      class="inflowpay-card-input inflowpay-card-cvc"
      placeholder="${this.options.placeholders.cvc}"
      maxlength="4"
      inputmode="numeric"
      autocomplete="cc-csc"
    />
  </div>

  <div class="inflowpay-error" id="error-card-number"></div>
  <div class="inflowpay-error" id="error-expiry"></div>
  <div class="inflowpay-error" id="error-cvc"></div>

  <!-- General Error -->
  <div class="inflowpay-general-error" id="general-error" style="display: none;">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12 8V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    <span class="inflowpay-general-error-text"></span>
  </div>

  <!-- Success Message -->
  <div class="inflowpay-general-success" id="general-success" style="display: none;">
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_189_171)">
        <path d="M6.99984 12.8334C10.2215 12.8334 12.8332 10.2217 12.8332 7.00008C12.8332 3.77842 10.2215 1.16675 6.99984 1.16675C3.77818 1.16675 1.1665 3.77842 1.1665 7.00008C1.1665 10.2217 3.77818 12.8334 6.99984 12.8334Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5.25 6.99992L6.41667 8.16659L8.75 5.83325" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
      </g>
      <defs>
        <clipPath id="clip0_189_171">
          <rect width="14" height="14" fill="white"/>
        </clipPath>
      </defs>
    </svg>
    <span class="inflowpay-general-success-text"></span>
  </div>

  <!-- Submit Button -->
  <button
    type="button"
    id="inflowpay-submit-btn"
    class="inflowpay-button"
  >
    <span class="inflowpay-button-text">${this.options.buttonText}</span>
    <span class="inflowpay-button-loader" style="display: none;">
      <div class="inflowpay-loader-animation"></div>
    </span>
  </button>

  <!-- Mandatory Disclaimer -->
  <div class="inflowpay-disclaimer">
    <svg class="inflowpay-disclaimer-icon" width="10" height="10" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.68893 3.29346H1.49704C1.1663 3.29346 0.898193 3.56157 0.898193 3.8923V5.98825C0.898193 6.31898 1.1663 6.58709 1.49704 6.58709H5.68893C6.01966 6.58709 6.28777 6.31898 6.28777 5.98825V3.8923C6.28777 3.56157 6.01966 3.29346 5.68893 3.29346Z" stroke="currentColor" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M2.09595 3.29342V2.09574C2.09595 1.69868 2.25368 1.31789 2.53444 1.03712C2.8152 0.756363 3.196 0.598633 3.59305 0.598633C3.99011 0.598633 4.3709 0.756363 4.65167 1.03712C4.93243 1.31789 5.09016 1.69868 5.09016 2.09574V3.29342" stroke="currentColor" stroke-width="0.7" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>

    <p class="inflowpay-disclaimer-text">
      ${getDisclaimerText(this.locale)}
    </p>
  </div>

</div>
`;
  }

  /**
   * Inject default CSS styles into shadow DOM
   */
  injectDefaultStyles(): void {
    const shadow = this.getShadowRoot();

    // Inject Google Fonts (DM Sans) in document.head (for global access)
    if (!document.getElementById('inflowpay-dm-sans-font')) {
      const link = document.createElement('link');
      link.id = 'inflowpay-dm-sans-font';
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap';
      document.head.appendChild(link);
    }

    // Check if styles are already injected in this shadow root
    if (shadow.querySelector('#inflowpay-card-element-styles')) {
      return;
    }

    // Add font link to shadow DOM so fonts are accessible
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href =
      'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap';
    shadow.appendChild(fontLink);

    const styleElement = document.createElement('style');
    styleElement.id = 'inflowpay-card-element-styles';
    styleElement.textContent = `
  /* InflowPay CardElement Default Styles */
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  .inflowpay-card-element {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    width: 344px;
    max-width: 100%;
    margin: 0 auto;
  }

  .inflowpay-card-inp-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background-color: #F5F5F5;
    padding: 8px;
    border-radius: 8px;
  }

  .inflowpay-card-input {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    background-color: transparent;
    border: 1px solid #CECECE;
    padding: 6px;
    border-radius: 6px;
    outline-width: 0;
    color: #000000;
    font-size: 16px;
    text-align: start;
  }

  .inflowpay-card-input::placeholder {
    color: #7A7A7A;
    text-align: start;
  }

  .inflowpay-card-input:focus,
  .inflowpay-card-input:focus-visible,
  .inflowpay-card-input:active {
    box-shadow: none !important;
  }

  .inflowpay-card-number {
    flex: 1;
    min-width: 0;
  }

  .inflowpay-card-expiry {
    width: 21.5%;
    flex-shrink: 0;
  }

  .inflowpay-card-cvc {
    width: 17.5%;
    flex-shrink: 0;
  }

  .inflowpay-error {
    display: none;
    font-size: 13px;
    color: #ef4444;
    text-align: start;
    margin-top: 6px;
    line-height: 1.4;
  }

  .inflowpay-general-error {
    padding: 12px;
    background-color: #FAC6C0;
    color: #FF443F;
    font-size: 14px;
    text-align: start;
    border-radius: 6px;
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .inflowpay-general-error svg {
    flex-shrink: 0;
  }

  .inflowpay-general-error-text {
    flex: 1;
  }

  .inflowpay-general-success {
    padding: 12px;
    background-color: #BFFFDD;
    color: #119959;
    font-size: 14px;
    text-align: start;
    border-radius: 6px;
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .inflowpay-general-success svg {
    flex-shrink: 0;
  }

  .inflowpay-general-success-text {
    flex: 1;
  }

  .inflowpay-button {
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    width: 100%;
    padding: 0 20px;
    height: 42px;
    font-size: 16px;
    font-weight: 500;
    color: #000000;
    background-color: #ADFFD2;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
  }

  .inflowpay-button:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  .inflowpay-button:active:not(:disabled) {
    transform: translateY(0);
  }

  .inflowpay-button:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .inflowpay-button-text {
    display: inline;
    line-height: 1;
  }

  .inflowpay-button-loader {
    display: none;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .inflowpay-loader-animation {
    width: 35px;
    aspect-ratio: 2;
    --_g: no-repeat radial-gradient(circle closest-side, #0F8259 90%, #0000);
    background: var(--_g) 0% 50%, var(--_g) 50% 50%, var(--_g) 100% 50%;
    background-size: calc(100%/3) 50%;
    animation: inflowpay-loader-animation 1s infinite linear;
  }

  @keyframes inflowpay-loader-animation {
    20% { background-position: 0% 0%, 50% 50%, 100% 50%; }
    40% { background-position: 0% 100%, 50% 0%, 100% 50%; }
    60% { background-position: 0% 50%, 50% 100%, 100% 0%; }
    80% { background-position: 0% 50%, 50% 50%, 100% 100%; }
  }

  .inflowpay-disclaimer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 10px;
    line-height: 1.5;
    text-align: center;
    color: #7A7A7A;
    margin-top: 16px;
  }

  .inflowpay-disclaimer-icon {
    flex-shrink: 0;
  }

  .inflowpay-disclaimer-text strong {
    font-weight: 600;
  }

  .inflowpay-disclaimer-text br {
    display: block;
  }

  @media (prefers-color-scheme: dark) {
    .inflowpay-card-inp-wrap { background-color: #2d2d2d; }
    .inflowpay-card-input { color: #ffffff; border-color: #7A7A7A; }
    .inflowpay-card-input::placeholder { color: #959499; }
    .inflowpay-disclaimer { color: #959499; }
  }
`;

    // Inject styles into shadow root
    shadow.appendChild(styleElement);
  }

  /**
   * Apply custom styles to the card element
   */
  applyCustomStyles(styles: CSSProperties): void {
    const shadow = this.getShadowRoot();
    const wrap = shadow.querySelector('.inflowpay-card-inp-wrap');
    const cardElement = shadow.querySelector('.inflowpay-card-element') as HTMLElement;

    // ========================================================================
    // 1. Apply global fontFamily
    // ========================================================================
    if (styles.fontFamily) {
      if (cardElement) {
        cardElement.style.fontFamily = styles.fontFamily;
      }
      const button = shadow.querySelector('.inflowpay-button') as HTMLElement;
      if (button) {
        button.style.fontFamily = styles.fontFamily;
      }
      const inputs = wrap?.querySelectorAll('.inflowpay-card-input');
      if (styles.fontFamily && inputs) {
        const fontFamily = styles.fontFamily;
        inputs.forEach((input) => {
          (input as HTMLElement).style.fontFamily = fontFamily;
        });
      }
    }

    if (!wrap) return;

    const htmlWrap = wrap as HTMLElement;

    // ========================================================================
    // 2. Apply input container styles (base styles)
    // ========================================================================
    if (styles.inputContainer) {
      const container = styles.inputContainer;
      if (container.backgroundColor) {
        htmlWrap.style.backgroundColor = container.backgroundColor;
      }
      if (container.border) {
        htmlWrap.style.border = container.border;
      }
      if (container.borderRadius) {
        htmlWrap.style.borderRadius = container.borderRadius;
      }
    }

    // ========================================================================
    // 3. Apply input field styles (base styles)
    // ========================================================================
    if (styles.input) {
      const inputs = wrap.querySelectorAll('.inflowpay-card-input');
      const inputStyles = styles.input;

      inputs.forEach((input) => {
        const htmlInput = input as HTMLElement;

        if (inputStyles.border) {
          htmlInput.style.border = inputStyles.border;
        }
        if (inputStyles.borderRadius) {
          htmlInput.style.borderRadius = inputStyles.borderRadius;
        }
        if (inputStyles.color) {
          htmlInput.style.color = inputStyles.color;
        }
        if (inputStyles.fontWeight) {
          htmlInput.style.fontWeight = inputStyles.fontWeight;
        }
      });

      // Apply placeholder styles
      if (inputStyles.placeholder?.color) {
        const styleId = 'inflowpay-custom-placeholder';
        let styleEl = shadow.querySelector(`#${styleId}`) as HTMLStyleElement;
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = styleId;
          shadow.appendChild(styleEl);
        }
        styleEl.textContent = `
          .inflowpay-card-input::placeholder {
            color: ${inputStyles.placeholder.color} !important;
          }
        `;
      }
    }

    // ========================================================================
    // 4. Apply theme-specific styles (light/dark mode)
    // ========================================================================
    // Apply custom theme styles if provided. Styles are scoped to their respective
    // themes using CSS media queries to follow system preference.
    if (styles.light || styles.dark) {
      const styleId = 'inflowpay-theme-styles';
      let themeStyleEl = shadow.querySelector(`#${styleId}`) as HTMLStyleElement;
      if (!themeStyleEl) {
        themeStyleEl = document.createElement('style');
        themeStyleEl.id = styleId;
        shadow.appendChild(themeStyleEl);
      }

      const cssRules: string[] = [];

      // Apply light mode custom styles (scoped to light mode only)
      if (styles.light) {
        const lightRules: string[] = [];
        this.applyThemeStyles(styles.light, lightRules, true);
        if (lightRules.length > 0) {
          cssRules.push(`  @media (prefers-color-scheme: light), (prefers-color-scheme: no-preference) {`);
          cssRules.push(...lightRules);
          cssRules.push(`  }`);
        }
      }

      // Apply dark mode custom styles (scoped to dark mode only)
      if (styles.dark) {
        const darkRules: string[] = [];
        this.applyThemeStyles(styles.dark, darkRules, true);
        if (darkRules.length > 0) {
          cssRules.push(`  @media (prefers-color-scheme: dark) {`);
          cssRules.push(...darkRules);
          cssRules.push(`  }`);
        }
      }

      if (cssRules.length > 0) {
        themeStyleEl.textContent = cssRules.join('\n');
      }
    }
  }

  /**
   * Helper method to apply theme styles (light or dark)
   * 
   * @param theme - Theme styles to apply
   * @param cssRules - Array to append CSS rules to
   * @param isDark - Whether styles are for dark mode (affects indentation for media queries)
   */
  private applyThemeStyles(
    theme: ThemeStyles,
    cssRules: string[],
    isDark: boolean
  ): void {
    // Indentation for CSS formatting (4 spaces for rules inside @media queries)
    const indent = isDark ? '    ' : '  ';

    // Apply input container theme styles
    if (theme.inputContainer) {
      const container = theme.inputContainer;
      if (container.backgroundColor) {
        cssRules.push(`${indent}.inflowpay-card-inp-wrap { background-color: ${container.backgroundColor} !important; }`);
      }
      if (container.border) {
        cssRules.push(`${indent}.inflowpay-card-inp-wrap { border: ${container.border} !important; }`);
      }
      if (container.borderRadius) {
        cssRules.push(`${indent}.inflowpay-card-inp-wrap { border-radius: ${container.borderRadius} !important; }`);
      }
    }

    // Apply input field theme styles
    if (theme.input) {
      const input = theme.input;
      if (input.border) {
        cssRules.push(`${indent}.inflowpay-card-input { border: ${input.border} !important; }`);
      }
      if (input.borderRadius) {
        cssRules.push(`${indent}.inflowpay-card-input { border-radius: ${input.borderRadius} !important; }`);
      }
      if (input.color) {
        cssRules.push(`${indent}.inflowpay-card-input { color: ${input.color} !important; }`);
      }
      if (input.fontWeight) {
        cssRules.push(`${indent}.inflowpay-card-input { font-weight: ${input.fontWeight} !important; }`);
      }
      if (input.placeholder?.color) {
        cssRules.push(`${indent}.inflowpay-card-input::placeholder { color: ${input.placeholder.color} !important; }`);
      }
    }
  }

  /**
   * Apply custom button styles
   */
  applyButtonStyles(
    styles: Partial<CSSStyleDeclaration> | ButtonStyle
  ): void {
    const shadow = this.getShadowRoot();
    const button = shadow.querySelector('.inflowpay-button') as HTMLElement;
    if (!button) return;

    // Check if it's a nested structure (with base, hover, disabled, loaderColor)
    if (
      'base' in styles ||
      'hover' in styles ||
      'disabled' in styles ||
      'loaderColor' in styles
    ) {
      const buttonStyles = styles as ButtonStyle;

      // Apply base styles directly to button
      if (buttonStyles.base) {
        Object.assign(button.style, buttonStyles.base);
      }

      // Build CSS for hover and disabled states
      const cssRules: string[] = [];

      if (buttonStyles.hover) {
        const hoverProps: string[] = [];
        Object.entries(buttonStyles.hover).forEach(([key, value]) => {
          if (value) {
            const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            hoverProps.push(`${cssKey}: ${value} !important`);
          }
        });
        if (hoverProps.length > 0) {
          cssRules.push(
            `.inflowpay-button:hover:not(:disabled) { ${hoverProps.join('; ')} }`
          );
        }
      }

      if (buttonStyles.disabled) {
        const disabledProps: string[] = [];
        Object.entries(buttonStyles.disabled).forEach(([key, value]) => {
          if (value) {
            const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            disabledProps.push(`${cssKey}: ${value} !important`);
          }
        });
        if (disabledProps.length > 0) {
          cssRules.push(
            `.inflowpay-button:disabled { ${disabledProps.join('; ')} }`
          );
        }
      }

      // Handle loader color
      if (buttonStyles.loaderColor) {
        const loaderColor = buttonStyles.loaderColor;
        cssRules.push(
          `.inflowpay-loader-animation { --_g: no-repeat radial-gradient(circle closest-side, ${loaderColor} 90%, #0000) !important; }`
        );
      }

      // Inject CSS for hover, disabled, and loader states into shadow root
      if (cssRules.length > 0) {
        const styleId = 'inflowpay-custom-button-states';
        let styleEl = shadow.querySelector(`#${styleId}`) as HTMLStyleElement;
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = styleId;
          shadow.appendChild(styleEl);
        }
        styleEl.textContent = cssRules.join('\n');
      }
    } else {
      // Flat structure - apply directly (backward compatible)
      Object.assign(button.style, styles as Partial<CSSStyleDeclaration>);
    }
  }

  /**
   * Get input element by ID from shadow DOM
   */
  getInput(id: string): HTMLInputElement {
    const shadow = this.getShadowRoot();
    return shadow.querySelector(`#${id}`) as HTMLInputElement;
  }

  /**
   * Get button element from shadow DOM
   */
  getButton(): HTMLButtonElement {
    const shadow = this.getShadowRoot();
    return shadow.querySelector('#inflowpay-submit-btn') as HTMLButtonElement;
  }

  /**
   * Reset shadow root reference (for cleanup)
   */
  resetShadowRoot(): void {
    this.shadowRoot = null;
  }
}
